import { useTranslation } from 'react-i18next';

import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import { useGlobal } from '~/hooks/useGlobal';
import type { BreadcrumbsItem } from '~/stores/CatalogStore';

import MobileHeaderLayout from '../../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../../Main/MobileHeader/MobileHeaderTitle';

import tp from '../textPage.module.scss';
import styles from './FAQPage.module.scss';

const content = {
  ru: {
    title: 'Часто задаваемые вопросы',
    payment: {
      title: 'Оплата',
      q1: 'Как оплатить заказ?',
      a1: 'Мы принимаем карты МИР, Visa, Mastercard. Оплата происходит на защищённой странице платёжной системы.',
      q2: 'Безопасна ли оплата?',
      a2: 'Да, все платежи защищены протоколом SSL и обрабатываются сертифицированными платёжными системами.',
    },
    delivery: {
      title: 'Доставка',
      q1: 'Сколько времени занимает доставка?',
      a1: 'Стандартная доставка — 12-16 дней с момента оформления заказа. Ускоренная доставка — 7-10 дней.',
      q2: 'Как отследить заказ?',
      a2: 'После отправки вы получите трек-номер для отслеживания посылки.',
    },
    returns: {
      title: 'Возврат',
      q1: 'Как вернуть товар?',
      a1: 'Вы можете вернуть товар в течение 14 дней после получения. Возврат средств происходит в течение 5-7 рабочих дней.',
    },
    authenticity: {
      title: 'Проверка подлинности',
      q1: 'Как проверить оригинальность товара?',
      a1: 'Каждый заказ комплектуется сертификатом подлинности и защитными бирками. Подробная инструкция по проверке приложена к посылке.',
    },
  },
  en: {
    title: 'Frequently Asked Questions',
    payment: {
      title: 'Payment',
      q1: 'How can I pay for my order?',
      a1: 'We accept Visa, Mastercard, and other major payment methods. Payment is processed on a secure payment gateway page.',
      q2: 'Is payment secure?',
      a2: 'Yes, all payments are protected by SSL protocol and processed by certified payment systems.',
    },
    delivery: {
      title: 'Delivery',
      q1: 'How long does delivery take?',
      a1: 'Standard delivery takes 12–16 days from the order date. Express delivery takes 7–10 days.',
      q2: 'How can I track my order?',
      a2: 'Once your order is shipped, you will receive a tracking number to monitor the delivery.',
    },
    returns: {
      title: 'Returns',
      q1: 'How do I return an item?',
      a1: 'You can return an item within 14 days of receipt. Refunds are processed within 5–7 business days.',
    },
    authenticity: {
      title: 'Authenticity Verification',
      q1: 'How can I verify the authenticity of a product?',
      a1: 'Each order comes with a certificate of authenticity and security tags. Detailed verification instructions are included in the package.',
    },
  },
};

const FAQPage = () => {
  const { t, i18n } = useTranslation();
  const { isTablet } = useGlobal();

  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const c = content[lang];

  return (
    <>
      <MobileHeaderLayout content={<MobileHeaderTitle text={t('faq')} />} />
      <div className="content-layout">
        <div className={tp.root}>
          {!isTablet && (
            <Breadcrumbs
              items={
                [
                  { text: t('home'), link: '/' },
                  { text: t('faq'), link: '/faq' },
                ].filter(Boolean) as BreadcrumbsItem[]
              }
            />
          )}

          <div className={tp.content}>
            <h1 className={tp.title}>{c.title}</h1>

            <section className={tp.section}>
              <h2 className={tp.subtitle}>{c.payment.title}</h2>

              <div className={styles.qaBlock}>
                <h3 className={styles.question}>{c.payment.q1}</h3>
                <p className={tp.text}>{c.payment.a1}</p>
              </div>

              <div className={styles.qaBlock}>
                <h3 className={styles.question}>{c.payment.q2}</h3>
                <p className={tp.text}>{c.payment.a2}</p>
              </div>
            </section>

            <section className={tp.section}>
              <h2 className={tp.subtitle}>{c.delivery.title}</h2>

              <div className={styles.qaBlock}>
                <h3 className={styles.question}>{c.delivery.q1}</h3>
                <p className={tp.text}>{c.delivery.a1}</p>
              </div>

              <div className={styles.qaBlock}>
                <h3 className={styles.question}>{c.delivery.q2}</h3>
                <p className={tp.text}>{c.delivery.a2}</p>
              </div>
            </section>

            <section className={tp.section}>
              <h2 className={tp.subtitle}>{c.returns.title}</h2>

              <div className={styles.qaBlock}>
                <h3 className={styles.question}>{c.returns.q1}</h3>
                <p className={tp.text}>{c.returns.a1}</p>
              </div>
            </section>

            <section className={tp.section}>
              <h2 className={tp.subtitle}>{c.authenticity.title}</h2>

              <div className={styles.qaBlock}>
                <h3 className={styles.question}>{c.authenticity.q1}</h3>
                <p className={tp.text}>{c.authenticity.a1}</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;
